import React from 'react';

interface TimelineStep {
    step: string;
    timestamp: string;
    note?: string;
}

interface CaseTimelineProps {
    steps: TimelineStep[];
}

export default function CaseTimeline({ steps }: CaseTimelineProps) {
    if (!steps || !Array.isArray(steps) || steps.length === 0) return (
        <div className="text-sm text-gray-500 italic">No timeline events recorded yet.</div>
    );

    // Sort by timestamp descending (newest first)
    const sortedSteps = [...steps].sort((a, b) => {
        const timeA = (a?.timestamp && !isNaN(new Date(a.timestamp).getTime())) ? new Date(a.timestamp).getTime() : 0;
        const timeB = (b?.timestamp && !isNaN(new Date(b.timestamp).getTime())) ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
    });

    const getIcon = (action: string) => {
        switch (action) {
            case 'case_created': return 'fa-folder-plus';
            case 'served_notice': return 'fa-envelope';
            case 'court_application': return 'fa-gavel';
            case 'outcome': return 'fa-info-circle';
            default: return 'fa-circle';
        }
    };

    const getLabel = (action: string) => {
        switch (action) {
            case 'case_created': return 'Case Created';
            case 'served_notice': return 'Notice Served';
            case 'court_application': return 'Court Application Filed';
            case 'outcome': return 'Case Outcome Recorded';
            default: return action.replace(/_/g, ' ');
        }
    };

    return (
        <div className="flow-root">
            <ul className="-mb-8">
                {sortedSteps.map((step, stepIdx) => (
                    <li key={stepIdx}>
                        <div className="relative pb-8">
                            {stepIdx !== sortedSteps.length - 1 ? (
                                <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                            ) : null}
                            <div className="relative flex space-x-3">
                                <div>
                                    <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${stepIdx === 0 ? 'bg-indigo-500' : 'bg-gray-400'}`}>
                                        <i className={`fas ${getIcon(step.step)} text-white text-sm`} aria-hidden="true" />
                                    </span>
                                </div>
                                <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                    <div>
                                        <p className="text-sm text-gray-900 font-medium">
                                            {getLabel(step.step)}
                                        </p>
                                        {step.note && (
                                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                                                {step.note}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                        {step.timestamp ? (
                                            <time dateTime={step.timestamp}>
                                                {new Date(step.timestamp).toLocaleDateString()}
                                                <span className="block text-xs">{new Date(step.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </time>
                                        ) : (
                                            <span className="text-xs text-gray-400">Date unknown</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
